import {
  Container,
  Heading,
  Box,
  Text,
  TableContainer,
  Table,
  Thead,
  Tbody,
  Tr,
  Td,
  Th,
  Grid,
  GridItem,
  useTheme,
  Select,
  HStack,
  RadioGroup,
  Radio,
  VStack,
  IconButton,
  Tooltip,
} from '@chakra-ui/react';
import Chart from 'react-apexcharts';
import { useRecoilValue } from 'recoil';
import CsvDownloader from 'react-csv-downloader';
import {
  FiDownload,
} from 'react-icons/fi';
import { Fragment, useEffect, useState } from 'react';
import { getPercent, roundToDecimal } from '@src/utils/math';
import { selectedDatabaseIdState } from '@transport-insights/uikit';
import { isEmpty } from 'lodash';
import LoadingSpinner from '@src/components/shared/LoadingSpinner';
import EmptyState from '@src/components/shared/EmptyState';
import MainLayout from '@src/components/layouts/main';
import { useReports } from '@src/modules/onf/context/reports';
import { useNetworkSelector } from '@src/modules/onf/context/network-selector';
import HnoFilter from '../components/shared/HnoFilter';

export default function NetworkCharacteristics() {
  const theme = useTheme();
  const [params, setParams] = useState({ networks: [], hno: [] });
  const { data: reportData, isLoading: isLoadingReport } = useReports('networkChar', params);
  const rcaId = useRecoilValue(selectedDatabaseIdState);
  const { data: selectorData, isLoading: isLoadingSelectorData } = useNetworkSelector();
  const [classification, setClassification] = useState('generalTraffic');
  const [selectedStreetFamily, setSelectedStreetFamily] = useState('Both');

  // Filter on classification so we can calculate totals for the classification even when street family filter is applied
  const filteredDataByClassification = reportData?.data?.filter(
    (x) => x.classification === classification,
  ) || [];

  // Further filter on street family if classification is not streetCategory
  const filteredData = filteredDataByClassification?.filter((x) => {
    if (classification === 'streetCategory') return true;
    return x.streetFamily === selectedStreetFamily;
  }) || [];

  const categories = [...new Set(filteredData?.map((x) => x.category))];

  // Urban Totals
  let urbanTotals = filteredDataByClassification.filter((x) => x.streetFamily === 'Urban')
    .reduce((acc, item) => ({
      ...acc,
      length: acc.length + item.length,
      sealed: acc.sealed + item.sealed,
      unsealed: acc.unsealed + item.unsealed,
      lane: acc.lane + item.lane,
      journeys: acc.journeys + item.journeys,
      heavyJourneys: acc.heavyJourneys + item.heavyJourneys,
    }), {
      length: 0, sealed: 0, unsealed: 0, lane: 0, journeys: 0, heavyJourneys: 0,
    });
  // Round them
  urbanTotals = {
    length: roundToDecimal(urbanTotals.length, 1),
    sealed: roundToDecimal(urbanTotals.sealed, 1),
    unsealed: roundToDecimal(urbanTotals.unsealed, 1),
    lane: roundToDecimal(urbanTotals.lane, 1),
    journeys: roundToDecimal(urbanTotals.journeys, 1),
    heavyJourneys: roundToDecimal(urbanTotals.heavyJourneys, 1),
  };
  // Rural Totals
  let ruralTotals = filteredDataByClassification.filter((x) => x.streetFamily === 'Rural')
    .reduce((acc, item) => ({
      ...acc,
      length: acc.length + item.length,
      sealed: acc.sealed + item.sealed,
      unsealed: acc.unsealed + item.unsealed,
      lane: acc.lane + item.lane,
      journeys: acc.journeys + item.journeys,
      heavyJourneys: acc.heavyJourneys + item.heavyJourneys,
    }), {
      length: 0, sealed: 0, unsealed: 0, lane: 0, journeys: 0, heavyJourneys: 0,
    });
  // Round them
  ruralTotals = {
    length: roundToDecimal(ruralTotals.length, 1),
    sealed: roundToDecimal(ruralTotals.sealed, 1),
    unsealed: roundToDecimal(ruralTotals.unsealed, 1),
    lane: roundToDecimal(ruralTotals.lane, 1),
    journeys: roundToDecimal(ruralTotals.journeys, 1),
    heavyJourneys: roundToDecimal(ruralTotals.heavyJourneys, 1),
  };

  // Both totals
  let bothTotals = filteredDataByClassification.filter((x) => x.streetFamily === 'Both')
    .reduce((acc, item) => ({
      ...acc,
      length: acc.length + item.length,
      sealed: acc.sealed + item.sealed,
      unsealed: acc.unsealed + item.unsealed,
      lane: acc.lane + item.lane,
      journeys: acc.journeys + item.journeys,
      heavyJourneys: acc.heavyJourneys + item.heavyJourneys,
    }), {
      length: 0, sealed: 0, unsealed: 0, lane: 0, journeys: 0, heavyJourneys: 0,
    });
  // Round them
  bothTotals = {
    length: roundToDecimal(bothTotals.length, 1),
    sealed: roundToDecimal(bothTotals.sealed, 1),
    unsealed: roundToDecimal(bothTotals.unsealed, 1),
    lane: roundToDecimal(bothTotals.lane, 1),
    journeys: roundToDecimal(bothTotals.journeys, 1),
    heavyJourneys: roundToDecimal(bothTotals.heavyJourneys, 1),
  };

  // Overall Totals
  let totals = filteredDataByClassification.filter((x) => x.streetFamily !== 'Both').reduce((acc, item) => ({
    ...acc,
    length: acc.length + item.length,
    sealed: acc.sealed + item.sealed,
    unsealed: acc.unsealed + item.unsealed,
    lane: acc.lane + item.lane,
    journeys: acc.journeys + item.journeys,
    heavyJourneys: acc.heavyJourneys + item.heavyJourneys,
  }), {
    length: 0, sealed: 0, unsealed: 0, lane: 0, journeys: 0, heavyJourneys: 0,
  });
  // Round them
  totals = {
    length: roundToDecimal(totals.length, 1),
    sealed: roundToDecimal(totals.sealed, 1),
    unsealed: roundToDecimal(totals.unsealed, 1),
    lane: roundToDecimal(totals.lane, 1),
    journeys: roundToDecimal(totals.journeys, 1),
    heavyJourneys: roundToDecimal(totals.heavyJourneys, 1),
  };

  const hasBoth = filteredDataByClassification.some((x) => x.streetFamily === 'Both');

  const urbanColor = theme.colors.gray[500];
  const ruralColor = theme.colors.orange[800];
  const bothColor = theme.colors.gray[800];

  const ruralCategoryCount = filteredDataByClassification.filter((x) => x.streetFamily === 'Rural').length;
  const urbanCategoryCount = filteredDataByClassification.filter((x) => x.streetFamily === 'Urban').length;
  const bothCategoryCount = filteredDataByClassification.filter((x) => x.streetFamily === 'Both').length;

  const handleApplyHnoFilter = (val) => {
    const hno = val ? [val] : [];
    setParams({ ...params, hno });
  };

  // Helper: Return the relevant totals based on the current classification and street family selection
  const getRelevantTotals = () => {
    if (classification === 'streetCategory') return totals;

    switch (selectedStreetFamily) {
      case 'Urban':
        return urbanTotals;
      case 'Rural':
        return ruralTotals;
      case 'Both':
        return bothTotals.length > 0 ? bothTotals : totals;
      default:
        return totals;
    }
  };

  const relevantTotals = getRelevantTotals();

  // Track if the user has scrolled down the page
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 0);
    };

    handleScroll(); // run once on mount
    window.addEventListener('scroll', handleScroll);

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (isLoadingReport || isLoadingSelectorData || isEmpty(reportData)) {
    return (
      <MainLayout>
        <LoadingSpinner />
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <Box
        py={2}
        px={4}
        maxW="full"
        position="sticky"
        top="80px"
        background="gray.50"
        zIndex="200"
        transition="box-shadow 0.2s ease"
        boxShadow={scrolled ? '0 4px 8px -2px rgba(0,0,0,0.15)' : 'none'}
        overflowX="clip"
      >
        <HStack justify="space-between" align="flex-start">
          <Heading as="h1">Network Characteristics</Heading>
          <HStack align="center">
            {classification !== 'streetCategory' && (
            <HStack direction="row" spacing={2} align="center" mr={2}>
              <Text fontWeight="normal" fontSize="xs" mr={2} textTransform="uppercase">Street family: </Text>
              <RadioGroup
                onChange={(val) => setSelectedStreetFamily(val)}
                value={selectedStreetFamily || ''}
              >
                <HStack direction="row" spacing={2}>
                  {[{ label: 'Both', value: 'Both' }, { label: 'Urban', value: 'Urban' }, { label: 'Rural', value: 'Rural' }].map((x) => (
                    <Radio key={x.value} value={x.value} size="sm">
                      {x.label}
                    </Radio>
                  ))}
                </HStack>
              </RadioGroup>
            </HStack>
            )}
            <VStack align="flex-end" spacing={2}>
              <Select
                size="sm"
                width="auto"
                value={classification}
                onChange={(e) => setClassification(e.target.value)}
              >
                <option value="generalTraffic">General Traffic</option>
                <option value="freight">Freight</option>
                <option value="streetCategory">Street Category</option>
              </Select>
            </VStack>
            <CsvDownloader
              filename={`${reportData.rcas[0]?.rcaName} - ${reportData.reportName} (${reportData?.lockYear.replace('/', '-')})`}
              extension=".csv"
              separator=","
              datas={filteredDataByClassification}
            >
              <Tooltip label="Download Report CSV" placement="left">
                <IconButton
                  icon={<FiDownload />}
                  borderRadius="full"
                  colorScheme="blue"
                  variant="outline"
                  ml={3}
                  isDisabled={filteredDataByClassification.length === 0}
                />
              </Tooltip>
            </CsvDownloader>
          </HStack>
        </HStack>
      </Box>
      <Container maxW="full">
        {Number.parseInt(rcaId, 10) === 81
          && (
          <Box
            p={4}
            mt={4}
            mb={2}
            background="white"
            border="1px solid"
            borderColor="gray.100"
            borderRadius="md"
            boxShadow="sm"
          >
            <Text fontWeight="bold" fontSize="md" mb={2}>State Highway Filter</Text>
            <HnoFilter
              selectedHno={(hno) => handleApplyHnoFilter(hno)}
              data={selectorData?.hnos}
              isLoading={isLoadingSelectorData}
              activeHnoFilter={reportData.reportParams?.hno[0]}
              buttonLabel="Apply"
            />
          </Box>
          )}
        {isEmpty(filteredData)
          && <EmptyState title="No Data" message="Sorry there is no data available for selected network" />}
        {!isEmpty(filteredData)
          && (
          <>
            <Box my={4}>
              <Grid templateColumns="repeat(3, 1fr)" gap={2}>
                <GridItem
                  rowSpan={2}
                  p={2}
                  background="white"
                  border="1px solid"
                  borderColor="gray.100"
                  borderRadius="md"
                  boxShadow="sm"
                  minH="280"
                >
                  <Chart
                    options={
                      {
                        title: {
                          text: 'Length by Category',
                        },
                        chart: {
                          stacked: true,
                          stackType: '100%',
                        },
                        xaxis: {
                          categories: [''],
                        },
                        yaxis: [
                          {
                            forceNiceScale: true,
                            tickAmount: 8,
                            reversed: true,
                            labels: {
                              formatter(val) {
                                return `${val ? val.toFixed(0) : val}%`;
                              },
                            },
                          },
                        ],
                        colors: categories.map((cat) => theme.colors.onfCategory[cat]?.base || 'gray.500'),
                        labels: categories,
                        tooltip: {
                          enabled: true,
                          y: {
                            formatter: (value) => `${value} km`,
                          },
                        },
                        legend: {
                          position: 'left',
                          formatter: (seriesName, opts) => `${seriesName} (${opts.w.globals.series[opts.seriesIndex]} km)`,
                          offsetY: 30,
                        },
                        dataLabels: {
                          formatter: (val, opts) => `${parseFloat(opts.w.globals.seriesPercent[opts.seriesIndex]).toFixed(0)}%`,
                        },
                      }
                    }
                    series={filteredData.map((x) => ({ data: [x.length.toFixed(1)], name: x.category }))}
                    type="bar"
                    width="100%"
                    height="100%"
                  />
                </GridItem>
                <GridItem
                  colSpan={2}
                  p={2}
                  background="white"
                  border="1px solid"
                  borderColor="gray.100"
                  borderRadius="md"
                  boxShadow="sm"
                  minH="380"
                >
                  <Chart
                    options={
                      {
                        title: {
                          text: 'Network Length vs Vehicle Journeys',
                        },
                        yaxis: [
                          {
                            forceNiceScale: true,
                            tickAmount: 10,
                            labels: {
                              formatter(val) {
                                return `${val.toFixed(0)}%`;
                              },
                            },
                          },
                        ],
                        xaxis: {
                          labels: {
                            style: {
                              colors: categories.map((cat) => theme.colors.onfCategory[cat]?.base || 'gray.500'),
                              fontWeight: 'bold',
                              fontSize: 10,
                            },
                          },
                        },
                        labels: categories,
                        tooltip: {
                          shared: true,
                          intersect: false,
                          enabled: true,
                        },
                        dataLabels: {
                          enabled: false,
                        },
                        legend: {
                          position: 'bottom',
                          itemMargin: {
                            vertical: 20,
                          },
                        },
                      }
                    }
                    series={[
                      {
                        name: 'Length',
                        data: filteredData.map((x) => getPercent(x.length, relevantTotals.length, 2)),
                      },
                      {
                        name: 'Journeys',
                        data: filteredData.map((x) => getPercent(x.journeys, relevantTotals.journeys, 2)),
                      },
                    ]}
                    type="bar"
                    width="100%"
                    height="100%"
                  />
                </GridItem>
                <GridItem
                  p={2}
                  background="white"
                  border="1px solid"
                  borderColor="gray.100"
                  borderRadius="md"
                  boxShadow="sm"
                  minH="320"
                >
                  <Chart
                    options={
                      {
                        title: {
                          text: 'Rural vs Urban',
                        },
                        labels: ['Rural', 'Urban'],
                        colors: [ruralColor, urbanColor],
                        tooltip: {
                          enabled: false,
                        },
                        legend: {
                          position: 'bottom',
                          formatter: (seriesName, opts) => `${seriesName} (${opts.w.globals.series[opts.seriesIndex].toFixed(1)} km)`,
                        },
                        dataLabels: {
                          formatter: (val) => `${Math.round(val)}%`,
                        },
                      }
                    }
                    series={
                      [
                        ruralTotals.length,
                        urbanTotals.length,
                      ]
                    }
                    type="pie"
                    width="100%"
                    height="100%"
                  />
                </GridItem>
                <GridItem
                  p={2}
                  background="white"
                  border="1px solid"
                  borderColor="gray.100"
                  borderRadius="md"
                  boxShadow="sm"
                  minH="320"
                >
                  <Chart
                    options={
                      {
                        title: {
                          text: 'Sealed vs Unsealed',
                        },
                        labels: ['Sealed', 'Unsealed'],
                        colors: ['#222222', '#666666'],
                        tooltip: {
                          enabled: false,
                        },
                        legend: {
                          position: 'bottom',
                          formatter: (seriesName, opts) => `${seriesName} (${opts.w.globals.series[opts.seriesIndex].toFixed(1)} km)`,
                        },
                        dataLabels: {
                          formatter: (val) => `${Math.round(val)}%`,
                        },
                      }
                    }
                    series={
                      [
                        filteredData.reduce((acc, item) => acc + item.sealed, 0),
                        filteredData.reduce((acc, item) => acc + item.unsealed, 0),
                      ]
                    }
                    type="pie"
                    width="100%"
                    height="100%"
                  />
                </GridItem>
              </Grid>
            </Box>
            <Box
              background="white"
              boxShadow="sm"
              border="1px solid"
              borderColor="gray.100"
              borderRadius="md"
              overflow="hidden"
              my={4}
            >
              <TableContainer>
                <Table variant="simple" size="md">
                  <Thead>
                    <Tr background="gray.50">
                      <Th />
                      <Th>ONF Category</Th>
                      <Th textAlign="right">Total Length (km)</Th>
                      <Th textAlign="right">Total Length (%)</Th>
                      <Th textAlign="right">Sealed (km)</Th>
                      <Th textAlign="right">Unsealed (km)</Th>
                      <Th textAlign="right">Lane (km)</Th>
                      <Th textAlign="right">Vehicle Journeys (M vkt)</Th>
                      <Th textAlign="right">Heavy Vehicle Journeys (M vkt)</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    <Tr>
                      <Td
                        rowSpan={urbanCategoryCount + 2}
                        maxWidth={2}
                        background={urbanColor}
                        padding={0}
                      >
                        <Text transform="rotate(-90deg)" textAlign="center" color="white" fontWeight="bold">URBAN</Text>
                      </Td>
                    </Tr>
                    {
                      filteredDataByClassification.filter((x) => x.streetFamily === 'Urban').map((row, i) => (
                        <Tr key={`cat_${i}`}>
                          <Td color={theme.colors.onfCategory[row.category]?.base || 'gray.500'} fontWeight="bold">
                            {row.category}
                          </Td>
                          <Td textAlign="right">
                            {roundToDecimal(row.length, 1)}
                            {' '}
                            km
                          </Td>
                          <Td textAlign="right">
                            {getPercent(row.length, totals.length)}
                            %
                          </Td>
                          <Td textAlign="right">
                            {roundToDecimal(row.sealed, 1)}
                            {' '}
                            km
                          </Td>
                          <Td textAlign="right">
                            {roundToDecimal(row.unsealed, 1)}
                            {' '}
                            km
                          </Td>
                          <Td textAlign="right">
                            {roundToDecimal(row.lane, 1)}
                            {' '}
                            km
                          </Td>
                          <Td textAlign="right">
                            {roundToDecimal(row.journeys, 1)}
                            M vkt
                          </Td>
                          <Td textAlign="right">
                            {roundToDecimal(row.heavyJourneys, 1)}
                            M vkt
                          </Td>
                        </Tr>
                      ))
                    }
                    <Tr fontWeight="bold" background={urbanColor} color="white">
                      <Td>
                        Total Urban Network
                      </Td>
                      <Td textAlign="right">
                        {urbanTotals.length}
                        {' '}
                        km
                      </Td>
                      <Td textAlign="right">
                        {getPercent(urbanTotals.length, totals.length)}
                        %
                      </Td>
                      <Td textAlign="right">
                        {urbanTotals.sealed}
                        {' '}
                        km
                      </Td>
                      <Td textAlign="right">
                        {urbanTotals.unsealed}
                        {' '}
                        km
                      </Td>
                      <Td textAlign="right">
                        {urbanTotals.lane}
                        {' '}
                        km
                      </Td>
                      <Td textAlign="right">
                        {urbanTotals.journeys}
                        M vkt
                      </Td>
                      <Td textAlign="right">
                        {urbanTotals.heavyJourneys}
                        M vkt
                      </Td>
                    </Tr>
                    <Tr>
                      <Td
                        rowSpan={ruralCategoryCount + 2}
                        maxWidth={2}
                        background={ruralColor}
                        padding={0}
                      >
                        <Text transform="rotate(-90deg)" textAlign="center" color="white" fontWeight="bold">RURAL</Text>
                      </Td>
                    </Tr>
                    {
                      filteredDataByClassification.filter((x) => x.streetFamily === 'Rural').map((row, i) => (
                        <Tr key={`cat_${i}`}>
                          <Td color={theme.colors.onfCategory[row.category]?.base || 'gray.500'} fontWeight="bold">
                            {row.category}
                          </Td>
                          <Td textAlign="right">
                            {roundToDecimal(row.length, 1)}
                            {' '}
                            km
                          </Td>
                          <Td textAlign="right">
                            {getPercent(row.length, totals.length)}
                            %
                          </Td>
                          <Td textAlign="right">
                            {roundToDecimal(row.sealed, 1)}
                            {' '}
                            km
                          </Td>
                          <Td textAlign="right">
                            {roundToDecimal(row.unsealed, 1)}
                            {' '}
                            km
                          </Td>
                          <Td textAlign="right">
                            {roundToDecimal(row.lane, 1)}
                            {' '}
                            km
                          </Td>
                          <Td textAlign="right">
                            {roundToDecimal(row.journeys, 1)}
                            M vkt
                          </Td>
                          <Td textAlign="right">
                            {roundToDecimal(row.heavyJourneys, 1)}
                            M vkt
                          </Td>
                        </Tr>
                      ))
                    }
                    <Tr fontWeight="bold" background={ruralColor} color="white">
                      <Td>
                        Total Rural Network
                      </Td>
                      <Td textAlign="right">
                        {ruralTotals.length}
                        {' '}
                        km
                      </Td>
                      <Td textAlign="right">
                        {getPercent(ruralTotals.length, totals.length)}
                        %
                      </Td>
                      <Td textAlign="right">
                        {ruralTotals.sealed}
                        {' '}
                        km
                      </Td>
                      <Td textAlign="right">
                        {ruralTotals.unsealed}
                        {' '}
                        km
                      </Td>
                      <Td textAlign="right">
                        {ruralTotals.lane}
                        {' '}
                        km
                      </Td>
                      <Td textAlign="right">
                        {ruralTotals.journeys}
                        M vkt
                      </Td>
                      <Td textAlign="right">
                        {ruralTotals.heavyJourneys}
                        M vkt
                      </Td>
                    </Tr>
                    {hasBoth && (
                    <>
                      <Tr>
                        <Td
                          rowSpan={bothCategoryCount + 2}
                          maxWidth={2}
                          background={bothColor}
                          padding={0}
                        >
                          <Text
                            transform="rotate(-90deg)"
                            textAlign="center"
                            color="white"
                            fontWeight="bold"
                          >
                            TOTAL
                          </Text>
                        </Td>
                      </Tr>

                      {filteredDataByClassification
                        .filter((x) => x.streetFamily === 'Both')
                        .map((row, i) => (
                          <Tr key={`cat_${i}`}>
                            <Td
                              color={theme.colors.onfCategory[row.category]?.base || 'gray.500'}
                              fontWeight="bold"
                            >
                              {row.category}
                            </Td>
                            <Td textAlign="right">
                              {roundToDecimal(row.length, 1)}
                              {' '}
                              km
                            </Td>
                            <Td textAlign="right">
                              {getPercent(row.length, totals.length)}
                              %
                            </Td>
                            <Td textAlign="right">
                              {roundToDecimal(row.sealed, 1)}
                              {' '}
                              km
                            </Td>
                            <Td textAlign="right">
                              {roundToDecimal(row.unsealed, 1)}
                              {' '}
                              km
                            </Td>
                            <Td textAlign="right">
                              {roundToDecimal(row.lane, 1)}
                              {' '}
                              km
                            </Td>
                            <Td textAlign="right">
                              {roundToDecimal(row.journeys, 1)}
                              M vkt
                            </Td>
                            <Td textAlign="right">
                              {roundToDecimal(row.heavyJourneys, 1)}
                              M vkt
                            </Td>
                          </Tr>
                        ))}

                      <Tr fontWeight="bold" background={bothColor} color="white">
                        <Td>Total Network</Td>
                        <Td textAlign="right">
                          {bothTotals.length}
                          {' '}
                          km
                        </Td>
                        <Td textAlign="right">
                          {getPercent(bothTotals.length, totals.length)}
                          %
                        </Td>
                        <Td textAlign="right">
                          {bothTotals.sealed}
                          {' '}
                          km
                        </Td>
                        <Td textAlign="right">
                          {bothTotals.unsealed}
                          {' '}
                          km
                        </Td>
                        <Td textAlign="right">
                          {bothTotals.lane}
                          {' '}
                          km
                        </Td>
                        <Td textAlign="right">
                          {bothTotals.journeys}
                          M vkt
                        </Td>
                        <Td textAlign="right">
                          {bothTotals.heavyJourneys}
                          M vkt
                        </Td>
                      </Tr>
                    </>
                    )}
                    {
                      filteredDataByClassification.filter((x) => x.category === 'Unclassified' && !hasBoth).map((row, i) => (
                        <Fragment key={`cat_${i}`}>
                          <Tr>
                            <Td rowSpan="2" maxWidth={2} background="gray.800" borderBottom="none" />
                          </Tr>
                          <Tr key={`cat_${i}`}>
                            <Td color={theme.colors.onfCategory[row.category]?.base || 'gray.500'} fontWeight="bold">
                              {row.category}
                            </Td>
                            <Td textAlign="right">
                              {roundToDecimal(row.length, 1)}
                              {' '}
                              km
                            </Td>
                            <Td textAlign="right">
                              {getPercent(row.length, totals.length)}
                              %
                            </Td>
                            <Td textAlign="right">
                              {roundToDecimal(row.sealed, 1)}
                              {' '}
                              km
                            </Td>
                            <Td textAlign="right">
                              {roundToDecimal(row.unsealed, 1)}
                              {' '}
                              km
                            </Td>
                            <Td textAlign="right">
                              {roundToDecimal(row.lane, 1)}
                              {' '}
                              km
                            </Td>
                            <Td textAlign="right">
                              {roundToDecimal(row.journeys, 1)}
                              M vkt
                            </Td>
                            <Td textAlign="right">
                              {roundToDecimal(row.heavyJourneys, 1)}
                              M vkt
                            </Td>
                          </Tr>
                        </Fragment>
                      ))
                    }
                    {!hasBoth && (
                    <Tr fontWeight="bold" background="gray.800" color="white">
                      <Td />
                      <Td>
                        Total Network
                      </Td>
                      <Td textAlign="right">
                        {totals.length}
                        {' '}
                        km
                      </Td>
                      <Td textAlign="right">
                        100%
                      </Td>
                      <Td textAlign="right">
                        {totals.sealed}
                        {' '}
                        km
                      </Td>
                      <Td textAlign="right">
                        {totals.unsealed}
                        {' '}
                        km
                      </Td>
                      <Td textAlign="right">
                        {totals.lane}
                        {' '}
                        km
                      </Td>
                      <Td textAlign="right">
                        {totals.journeys}
                        M vkt
                      </Td>
                      <Td textAlign="right">
                        {totals.heavyJourneys}
                        M vkt
                      </Td>
                    </Tr>
                    )}
                  </Tbody>
                </Table>
              </TableContainer>
            </Box>
          </>
          )}
      </Container>
    </MainLayout>
  );
}
